/**
 * Browser-local memory of the last BIM-Hub Model this fixture's D3 proposal revisions were saved
 * into. Not authoritative -- see `plan/product-boundary.md`: BIM-Hub owns Project/Model/ModelRevision
 * truth, Workbench only remembers where its own last save landed so the next save can pass the right
 * `base_revision` instead of re-creating a Model every time (see the GO answer, "初回はBIM-Hubに
 * personal projectを作り…次回以降はcurrent manifestを取得し、そのrevisionをbaseにする").
 *
 * `localStorage`, not the device-local SQLite ledger in `integration/local-draft-persistence.ts`:
 * that module persists an in-memory Draft ledger for the Tauri desktop shell only. This is plain
 * browser (and Tauri webview) state, non-sensitive (a project id, a model id, a revision number --
 * no token, no BIM-Hub content), and every existing precedent for that kind of ephemeral
 * UI-preference state in this app (`plan/product-boundary.md`'s "Workbench UI preference") is a
 * lightweight per-key store rather than a native plugin, so this mirrors that with `window.localStorage`
 * directly instead of introducing a new persistence layer for three scalars.
 */

export interface D3KnownSaveTarget {
  readonly projectId: string;
  readonly modelId: string;
  readonly revision: number;
}

const STORAGE_KEY_PREFIX = "aec-workbench:d3-save-target:";

function storageKey(fixtureId: string): string {
  return `${STORAGE_KEY_PREFIX}${fixtureId}`;
}

function isValidTarget(value: unknown): value is D3KnownSaveTarget {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.projectId === "string" && candidate.projectId.trim() !== "" &&
    typeof candidate.modelId === "string" && candidate.modelId.trim() !== "" &&
    typeof candidate.revision === "number" && Number.isSafeInteger(candidate.revision) && candidate.revision > 0
  );
}

/** Returns `null` on anything not exactly the expected shape, or when storage is unavailable (private-browsing quota, etc.). */
export function loadKnownD3SaveTarget(fixtureId: string, storage: Storage = window.localStorage): D3KnownSaveTarget | null {
  try {
    const raw = storage.getItem(storageKey(fixtureId));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidTarget(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Best-effort: a failed write (quota, private browsing, disabled storage) only means a future reload re-creates a Model on next save. It never blocks the save that already succeeded. */
export function persistKnownD3SaveTarget(fixtureId: string, target: D3KnownSaveTarget, storage: Storage = window.localStorage): void {
  try {
    storage.setItem(storageKey(fixtureId), JSON.stringify(target));
  } catch {
    // Best-effort only; see doc comment above.
  }
}

export function clearKnownD3SaveTarget(fixtureId: string, storage: Storage = window.localStorage): void {
  try {
    storage.removeItem(storageKey(fixtureId));
  } catch {
    // Best-effort only.
  }
}
